import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const PRODUCTS = [
  { img: "/images/product-01.jpg", ref: "OG-1962-STB", name: "Starburst Wall Clock", era: "1960s", price: "$275", tag: "Popular", span: "lg:col-span-5", ratio: "aspect-square" },
  { img: "/images/product-04.jpg", ref: "OG-1948-BRK", name: "Brass Key Ring Set", era: "1940s–60s", price: "$55", tag: "Vetted", span: "lg:col-span-7", ratio: "aspect-[4/5] lg:aspect-[16/10]" },
  { img: "/images/product-08.jpg", ref: "OG-1984-LTJ", name: "Leather Travel Journal", era: "1980s", price: "$110", tag: "Rare", span: "lg:col-span-4", ratio: "aspect-[4/5]" },
  { img: "/images/product-12.jpg", ref: "OG-1963-TMR", name: "Teak Magazine Rack", era: "1960s", price: "$210", tag: "Teak", span: "lg:col-span-4", ratio: "aspect-[4/5]" },
  { img: "/images/product-16.jpg", ref: "OG-1955-AGL", name: "Art Glass Table Lamp", era: "1950s", price: "$340", tag: "Tested", span: "lg:col-span-4", ratio: "aspect-[4/5]" },
];

function ProductCard({ product, index }) {
  const ref = useRef(null);

  const spotlight = (e) => {
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--sx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--sy", `${e.clientY - r.top}px`);
  };

  return (
    <motion.article
      data-testid={`product-card-${index + 1}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className={`group col-span-12 sm:col-span-6 ${product.span}`}
    >
      <button
        data-testid={`product-modal-trigger-${index + 1}`}
        onClick={() => toast(`${product.name} — full archival record publishes at launch.`)}
        className="block w-full cursor-pointer text-left"
      >
        <div
          ref={ref}
          onMouseMove={spotlight}
          className="relative overflow-hidden border border-[#E2D9CD] bg-[#FCF9F5]"
        >
          <img
            src={product.img}
            alt={`${product.name}, ${product.era}`}
            loading="lazy"
            className={`${product.ratio} w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]`}
          />
          <div
            className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            style={{ background: "radial-gradient(240px circle at var(--sx, 50%) var(--sy, 50%), rgba(197,155,39,0.18), transparent 65%)" }}
          />
          <div className="absolute left-3 top-3 border border-[#E2D9CD] bg-[#FAF6F0]/90 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B1D2F] backdrop-blur-sm">
            {product.tag}
          </div>
          <div className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center border border-[#1C1815] bg-[#FAF6F0] opacity-0 transition-all duration-300 group-hover:opacity-100">
            <ArrowUpRight className="h-4 w-4 text-[#1C1815]" />
          </div>
        </div>
        <div className="mt-4 flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8A8075]">
              {product.ref} · {product.era}
            </p>
            <h3 className="mt-1 font-serif text-xl text-[#1C1815] transition-colors duration-300 group-hover:text-[#6B1D2F]">
              {product.name}
            </h3>
          </div>
          <p className="font-serif text-lg text-[#1C1815]">{product.price}</p>
        </div>
      </button>
    </motion.article>
  );
}

export default function ArchivalTeaserGrid() {
  return (
    <section data-testid="archival-teaser-grid" className="mx-auto max-w-[1600px] px-6 py-24 sm:px-12 lg:px-20 lg:py-32">
      <div className="mb-14 grid grid-cols-12 items-end gap-6">
        <div className="col-span-12 lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mb-4 flex items-center gap-3 font-mono text-[11px] font-medium uppercase tracking-[0.3em] text-[#6B1D2F]"
          >
            <span className="inline-block h-px w-10 bg-[#6B1D2F]" />
            From the Vault — A Preview
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-3xl tracking-tight text-[#1C1815] sm:text-4xl lg:text-5xl"
          >
            Fifteen pieces are shelved.
            <br />
            <span className="italic text-[#6B1D2F]">Five are allowed out early.</span>
          </motion.h2>
        </div>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="col-span-12 max-w-sm text-sm leading-relaxed text-[#8A8075] lg:col-span-5 lg:justify-self-end"
        >
          Demonstration listings from $55 to $450, each with era notes and honest condition
          grading. Full archival records publish the day we open.
        </motion.p>
      </div>

      <div className="grid grid-cols-12 gap-8 lg:gap-12">
        {PRODUCTS.map((p, i) => (
          <ProductCard key={p.ref} product={p} index={i} />
        ))}
      </div>
    </section>
  );
}
